import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, UserPlus, Users2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SectionAssignModal from "../components/SectionAssignModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January 2026", "February 2026", "March 2026", "April 2026",
  "May 2026", "June 2026", "July 2026", "August 2026",
  "September 2026", "October 2026", "November 2026", "December 2026"
];

const CURRENT_MONTH = (() => {
  const names = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const d = new Date();
  return `${names[d.getMonth()]} ${d.getFullYear()}`;
})();

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed"];
const PRODUCT_TYPES = ["Mock", "Magazine"];

const autoDetectProductType = (particulars = "") => {
  const p = String(particulars).toLowerCase();
  if (p.includes("mock")) return "Mock";
  if (p.includes("magazine")) return "Magazine";
  return "";
};

const ClientOrders = () => {
  const { clientId } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [masterData, setMasterData] = useState({ exams: [], subjects: [] });
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [sectionModalOrder, setSectionModalOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderForm, setOrderForm] = useState({
    exam: "",
    subject: "",
    particulars: "",
    quantity: "",
    details: "",
    status: "Pending",
    delivered_on: "",
    rate: 0,
    realised: 0,
    assigned_to: "",
    product_type: ""
  });

  const isAdmin = user?.role === "admin";
  const isContentManager = user?.role === "content_manager";
  const canEdit = isAdmin || isContentManager;
  const canViewFinancials = isAdmin;

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/orders?client_id=${clientId}&month=${encodeURIComponent(selectedMonth)}`),
        axios.get(`${API}/masterdata`)
      ];
      
      // Only fetch assignable users if can assign
      if (canEdit) {
        requests.push(axios.get(`${API}/assignable-users`));
      }
      
      const responses = await Promise.all(requests);
      setClient(responses[0].data);
      setOrders(responses[1].data);
      
      const exams = responses[2].data.filter(d => d.category === "exam").map(d => d.value);
      const subjects = responses[2].data.filter(d => d.category === "subject").map(d => d.value);
      setMasterData({ exams, subjects });
      
      if (canEdit && responses[3]) {
        setAssignableUsers(responses[3].data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedMonth, canEdit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderForm.exam || !orderForm.particulars) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const payload = {
        ...orderForm,
        client_id: clientId,
        month: selectedMonth,
        rate: canViewFinancials ? parseFloat(orderForm.rate) || 0 : undefined,
        realised: canViewFinancials ? parseFloat(orderForm.realised) || 0 : undefined
      };

      // Remove financial fields for managers
      if (!canViewFinancials) {
        delete payload.rate;
        delete payload.realised;
      }

      if (editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, payload);
        toast.success("Order updated");
      } else {
        await axios.post(`${API}/orders`, payload);
        toast.success("Order created");
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save order");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setOrderForm({
      exam: order.exam,
      subject: order.subject,
      particulars: order.particulars,
      quantity: order.quantity || "",
      details: order.details || "",
      status: order.status,
      delivered_on: order.delivered_on || "",
      rate: order.rate || 0,
      realised: order.realised || 0,
      assigned_to: order.assigned_to || "",
      product_type: order.product_type || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleAssign = (order) => {
    // Multi-section orders use the section modal; legacy orders use the single-user modal
    if (order.product_type === "Mock" || order.product_type === "Magazine") {
      setSectionModalOrder(order);
      return;
    }
    setSelectedOrder(order);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (userId) => {
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, { assigned_to: userId || null });
      toast.success(userId ? "Task assigned successfully" : "Task unassigned");
      setShowAssignModal(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign task");
    }
  };

  const handleInlineUpdate = async (orderId, field, value) => {
    // Check if manager trying to update financial field
    if (!canViewFinancials && (field === "rate" || field === "realised")) {
      toast.error("You don't have permission to edit financials");
      return;
    }
    
    try {
      await axios.put(`${API}/orders/${orderId}`, { [field]: value });
      fetchData();
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const resetForm = () => {
    setEditingOrder(null);
    setOrderForm({
      exam: "",
      subject: "",
      particulars: "",
      quantity: "",
      details: "",
      status: "Pending",
      delivered_on: "",
      rate: 0,
      realised: 0,
      assigned_to: "",
      product_type: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const employeeMap = useMemo(() => {
    const m = {};
    assignableUsers.forEach((e) => { m[e.id] = e; });
    return m;
  }, [assignableUsers]);

  const renderAssigneesSummary = (order) => {
    if (order.product_type === "Mock" || order.product_type === "Magazine") {
      const sub = order.sub_assignments || [];
      const ids = Array.from(new Set(sub.map((s) => s.assigned_to).filter(Boolean)));
      const names = ids.map((id) => employeeMap[id]?.name).filter(Boolean);
      const done = sub.filter((s) => s.task_status === "Finished").length;
      if (!sub.length || !names.length) return <span className="text-slate-400 text-xs">Unassigned</span>;
      const shown = names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
      return (
        <div className="flex flex-col">
          <span className="text-xs text-slate-700">{shown}</span>
          <span className="text-[10px] text-slate-500">{done}/{sub.length} done</span>
        </div>
      );
    }
    const nm = employeeMap[order.assigned_to]?.name;
    return nm ? <span className="text-xs text-slate-700">{nm}</span> : <span className="text-slate-400 text-xs">Unassigned</span>;
  };

  // Calculate totals (only for admin)
  const totalRevenue = canViewFinancials ? orders.reduce((sum, o) => sum + (o.rate || 0), 0) : 0;
  const totalRealised = canViewFinancials ? orders.reduce((sum, o) => sum + (o.realised || 0), 0) : 0;
  const completedOrders = orders.filter(o => o.status === "Completed").length;

  const getUserName = (userId) => {
    const user = assignableUsers.find(u => u.id === userId);
    return user?.name || "Unassigned";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Client not found</p>
        <Link to="/" className="text-prepify-600 hover:underline">Back to Dashboard</Link>
      </div>
    );
  }

  const examOptions = masterData.exams.length > 0 ? masterData.exams : ["CLAT", "AILET", "CUET", "Other"];
  const subjectOptions = masterData.subjects.length > 0 ? masterData.subjects : ["GK", "Legal Reasoning", "Other"];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="btn-ghost p-2">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {client.name}
            </h1>
            <p className="text-sm text-slate-500">{client.email}</p>
          </div>
        </div>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-44 bg-white" data-testid="month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((month) => (
              <SelectItem key={month} value={month}>{month}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats - Only show financials to admin */}
      <div className={`grid grid-cols-1 ${canViewFinancials ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-4`}>
        {canViewFinancials && (
          <>
            <div className="stat-card">
              <p className="stat-card-title">Total Revenue</p>
              <p className="stat-card-value primary">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Revenue Realised</p>
              <p className="stat-card-value success">{formatCurrency(totalRealised)}</p>
              <p className="stat-card-subtitle">
                {totalRevenue > 0 ? Math.round((totalRealised / totalRevenue) * 100) : 0}% collected
              </p>
            </div>
          </>
        )}
        <div className="stat-card">
          <p className="stat-card-title">Total Orders</p>
          <p className="stat-card-value">{orders.length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card-title">Completed</p>
          <p className="stat-card-value success">{completedOrders}</p>
          <p className="stat-card-subtitle">{orders.length - completedOrders} pending</p>
        </div>
      </div>

      {/* Orders Table */}
      <div className="stat-card p-0">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Orders - {selectedMonth.split(" ")[0]}
          </h2>
          {canEdit && (
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="btn-primary text-sm"
              data-testid="add-order-btn"
            >
              <Plus size={16} /> Add Order
            </button>
          )}
        </div>

        {orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Exam</th>
                  <th>Subject</th>
                  <th>Particulars</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Assignees</th>
                  <th>Task Status</th>
                  {canViewFinancials && <th>Revenue</th>}
                  {canViewFinancials && <th>Realised</th>}
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => (
                  <tr key={order.id} data-testid={`order-row-${order.id}`}>
                    <td className="font-mono text-slate-500">{idx + 1}</td>
                    <td>{order.exam}</td>
                    <td>{order.subject}</td>
                    <td>{order.particulars}</td>
                    <td>
                      {order.product_type ? (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          order.product_type === "Mock" ? "bg-prepify-100 text-prepify-700" :
                          order.product_type === "Magazine" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>{order.product_type}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="font-mono">{order.quantity || "-"}</td>
                    <td>
                      {canEdit ? (
                        <select
                          value={order.status}
                          onChange={(e) => handleInlineUpdate(order.id, "status", e.target.value)}
                          className={`status-select ${
                            order.status === "Completed" ? "completed" :
                            order.status === "In Progress" ? "progress" : "pending"
                          }`}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <span className={`badge ${
                          order.status === "Completed" ? "badge-success" :
                          order.status === "In Progress" ? "badge-info" : "badge-warning"
                        }`}>
                          {order.status}
                        </span>
                      )}
                    </td>
                    <td>
                      {renderAssigneesSummary(order)}
                    </td>
                    <td>
                      <span className={`badge ${
                        order.task_status === "Finished" ? "badge-success" :
                        order.task_status === "Started" ? "badge-info" : "badge-neutral"
                      }`}>
                        {order.task_status || "Not Started"}
                      </span>
                    </td>
                    {canViewFinancials && (
                      <td className="font-mono">{formatCurrency(order.rate)}</td>
                    )}
                    {canViewFinancials && (
                      <td className="font-mono text-green-600">{formatCurrency(order.realised)}</td>
                    )}
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleAssign(order)}
                            className="btn-ghost text-prepify-600 p-1 inline-flex items-center gap-1"
                            title={order.product_type === "Mock" || order.product_type === "Magazine" ? "Assign sections to writers" : "Assign Task"}
                            data-testid={`assign-order-${order.id}`}
                          >
                            {order.product_type === "Mock" || order.product_type === "Magazine" ? (
                              <Users2 size={16} />
                            ) : (
                              <UserPlus size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(order)}
                            className="btn-ghost text-slate-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="btn-danger"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            No orders for {selectedMonth}
          </div>
        )}
      </div>

      {/* Add/Edit Order Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingOrder ? "Edit Order" : "Add New Order"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Exam *</Label>
                <Select value={orderForm.exam} onValueChange={(v) => setOrderForm({...orderForm, exam: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {examOptions.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <Select value={orderForm.subject} onValueChange={(v) => setOrderForm({...orderForm, subject: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Particulars *</Label>
              <Input
                value={orderForm.particulars}
                onChange={(e) => {
                  const v = e.target.value;
                  setOrderForm((prev) => ({
                    ...prev,
                    particulars: v,
                    product_type: prev.product_type || autoDetectProductType(v),
                  }));
                }}
                placeholder="e.g., Advanced Level Mock"
              />
            </div>
            <div className="space-y-2">
              <Label>Product Type</Label>
              <Select value={orderForm.product_type || "__none__"} onValueChange={(v) => setOrderForm({...orderForm, product_type: v === "__none__" ? "" : v})}>
                <SelectTrigger data-testid="client-order-form-product-type">
                  <SelectValue placeholder="Other" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Other (single assignee)</SelectItem>
                  {PRODUCT_TYPES.map((p) => (
                    <SelectItem key={p} value={p}>{p} (multi-section)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(orderForm.product_type === "Mock" || orderForm.product_type === "Magazine") && (
                <p className="text-xs text-slate-500 bg-prepify-50 border border-prepify-100 rounded-lg px-3 py-2">
                  Multi-section product. {editingOrder ? "Use the Assign button on the row to set writers per section." : "Save the order first, then click Assign on the row to set writers per section."}
                </p>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  value={orderForm.quantity}
                  onChange={(e) => setOrderForm({...orderForm, quantity: e.target.value})}
                  placeholder="e.g., 50 Questions"
                />
              </div>
              {canViewFinancials && (
                <>
                  <div className="space-y-2">
                    <Label>Revenue (₹)</Label>
                    <Input
                      type="number"
                      value={orderForm.rate}
                      onChange={(e) => setOrderForm({...orderForm, rate: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Realised (₹)</Label>
                    <Input
                      type="number"
                      value={orderForm.realised}
                      onChange={(e) => setOrderForm({...orderForm, realised: e.target.value})}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={orderForm.status} onValueChange={(v) => setOrderForm({...orderForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Date</Label>
                <Input
                  type="date"
                  value={orderForm.delivered_on}
                  onChange={(e) => setOrderForm({...orderForm, delivered_on: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select value={orderForm.assigned_to} onValueChange={(v) => setOrderForm({...orderForm, assigned_to: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {assignableUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingOrder ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Task Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Assign &quot;{selectedOrder?.particulars}&quot; to:
            </p>
            <div className="space-y-2">
              <button
                onClick={() => handleAssignSubmit(null)}
                className="w-full p-3 text-left rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <span className="text-slate-500">Unassign</span>
              </button>
              {assignableUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleAssignSubmit(u.id)}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    selectedOrder?.assigned_to === u.id
                      ? 'border-prepify-500 bg-prepify-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="font-medium text-slate-900">{u.name}</span>
                  <span className="text-sm text-slate-500 ml-2 capitalize">({u.role})</span>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Section assign modal (Mocks/Magazines) */}
      <SectionAssignModal
        open={!!sectionModalOrder}
        order={sectionModalOrder}
        employees={assignableUsers}
        onClose={() => setSectionModalOrder(null)}
        onSaved={() => fetchData()}
      />
    </div>
  );
};

export default ClientOrders;
